import React from 'react';
import Plot from 'react-plotly.js';
import MetricCard from './MetricCard';

/**
 * Renders a widget visualization based on viz_config and data.
 *
 * Props:
 *   data     - array of dicts (the result from widget execution)
 *   vizConfig - { type, title, x_axis, y_axis, color, layout }
 */
function WidgetRenderer({ data, vizConfig }) {
  if (!data || !vizConfig) {
    return <div style={{ padding: '20px', color: '#999', textAlign: 'center' }}>No data</div>;
  }

  const { type, title, x_axis, y_axis, color, layout = {} } = vizConfig;

  const defaultLayout = {
    title: { text: title || '', y: 0.95, yanchor: 'top', font: { size: 16 } },
    paper_bgcolor: '#FFFFFF',
    height: 420,
    margin: { t: 80, r: 20, b: 60, l: 60 },
    ...layout,
  };

  const plotConfig = {
    responsive: true,
    displayModeBar: true,
    modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d', 'autoScale2d', 'toggleSpikelines', 'zoom2d'],
    displaylogo: false,
  };

  switch (type) {
    case 'bar':
      return (
        <Plot
          data={[{
            x: data.map(d => d[x_axis]),
            y: data.map(d => d[y_axis]),
            type: 'bar',
            marker: color ? { color: data.map(d => d[color]) } : { color: '#1e3a5f' },
          }]}
          layout={defaultLayout}
          config={plotConfig}
          style={{ width: '100%', height: '420px' }}
        />
      );

    case 'line':
      return (
        <Plot
          data={[{
            x: data.map(d => d[x_axis]),
            y: data.map(d => d[y_axis]),
            type: 'scatter',
            mode: 'lines+markers',
            line: { color: '#1e3a5f' },
          }]}
          layout={defaultLayout}
          config={plotConfig}
          style={{ width: '100%', height: '420px' }}
        />
      );

    case 'pie':
      return (
        <Plot
          data={[{
            labels: data.map(d => d[x_axis]),
            values: data.map(d => d[y_axis]),
            type: 'pie',
          }]}
          layout={defaultLayout}
          config={plotConfig}
          style={{ width: '100%', height: '420px' }}
        />
      );

    case 'scatter':
      return (
        <Plot
          data={[{
            x: data.map(d => d[x_axis]),
            y: data.map(d => d[y_axis]),
            type: 'scatter',
            mode: 'markers',
            marker: { color: '#1e3a5f', size: 8 },
          }]}
          layout={defaultLayout}
          config={plotConfig}
          style={{ width: '100%', height: '420px' }}
        />
      );

    case 'table': {
      if (!data.length) return <div style={{ padding: '20px', color: '#999' }}>No data</div>;
      const columns = Object.keys(data[0]);
      return (
        <div style={{ width: '100%', overflowX: 'auto' }}>
          {title && <h4 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>{title}</h4>}
          <table className="data-table" style={{ width: '100%', minWidth: '320px' }}>
            <thead>
              <tr>
                {columns.map(col => <th key={col}>{col}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.slice(0, 100).map((row, i) => (
                <tr key={i}>
                  {columns.map(col => <td key={col}>{row[col] != null ? String(row[col]) : '-'}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          {data.length > 100 && (
            <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '8px' }}>
              Showing first 100 of {data.length} rows
            </p>
          )}
        </div>
      );
    }

    case 'kpi_card': {
      const item = Array.isArray(data) ? data[0] : data;
      if (!item) return <div style={{ padding: '20px', color: '#999' }}>No data</div>;
      return (
        <MetricCard
          label={item.label || title || 'Metric'}
          value={item.value != null ? item.value : '-'}
          type={typeof item.value === 'number' && item.value <= 1 ? 'percent' : 'number'}
          change={item.change != null ? { text: `${Math.abs(item.change)}%`, positive: item.change >= 0 } : null}
        />
      );
    }

    default:
      return <div style={{ padding: '20px', color: '#999' }}>Unknown chart type: {type}</div>;
  }
}

export default WidgetRenderer;
